/* eslint-disable react/jsx-curly-brace-presence */
import { Divider, Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import { Link } from 'react-router-dom';

import { Icon } from 'packages/common-frontend/components/icons';
import {
  MenuTree,
  MenuTreeItem,
} from 'packages/common-frontend/components/MenuTree';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { ComponentGridRenderer } from '~/core/routes/dev/ComponentGridRenderer';
import {
  PageTemplate,
  SectionHeader,
  SectionText,
} from '~/core/routes/dev/PageTemplate';

// Constants
const COMPONENT_NAME = './common-frontend/MenuTree';
const veryLong = 'very long very long very long very long very long';

// Fragments
const RouteBase = () => {
  const borderColor = useColorModeValue('gray.100', 'gray.800');
  return (
    <PageTemplate title={COMPONENT_NAME}>
      <ComponentGridRenderer
        primaries={['v2-aurora', 'gray'] as const}
        primaryPropName="colorScheme"
        secondaries={['md', 'sm'] as const}
        secondaryPropName="size"
        tertiaries={[null]}
        width="18.75rem"
      >
        {({ primary: colorScheme, secondary: size }) => {
          const commonProps = {
            colorScheme,
            size,
            defaultIsOpen: false,
          };

          return (
            <Flex direction="column" gap="0.5rem" width="100%">
              <SectionText>
                defaultIsOpen={`"${String(commonProps.defaultIsOpen)}"`}
              </SectionText>
              <MenuTree
                FlexProps={{
                  borderWidth: '1px',
                  borderColor,
                  borderRadius: '0.5rem',
                  padding: '0.75rem',
                }}
              >
                <MenuTreeItem
                  {...commonProps}
                  label="Home"
                  icon={<Icon icon="home" />}
                  isActive
                />
                <MenuTreeItem
                  {...commonProps}
                  label="Tasks"
                  icon={<Icon icon="list" />}
                />
                <MenuTreeItem
                  {...commonProps}
                  isDisabled
                  label={`Insights (disabled group) `}
                  icon={<Icon icon="chart-pie-simple" />}
                >
                  <MenuTreeItem {...commonProps} label={`Tasks ${veryLong}`}>
                    <MenuTreeItem {...commonProps} label={`Tasks`} />
                    <MenuTreeItem {...commonProps} label={`Portfolio`} />
                  </MenuTreeItem>
                  <MenuTreeItem {...commonProps} label="Portfolio" />
                </MenuTreeItem>
                <MenuTreeItem
                  {...commonProps}
                  label={`Insights ${veryLong} `}
                  icon={<Icon icon="chart-pie-simple" />}
                  expandedStoreKey={`${COMPONENT_NAME}.${colorScheme}.${size}`}
                >
                  <MenuTreeItem {...commonProps} label={`Tasks ${veryLong}`}>
                    <MenuTreeItem
                      {...commonProps}
                      label={`Tasks ${veryLong}`}
                    />
                    <MenuTreeItem
                      {...commonProps}
                      label={`Portfolio ${veryLong}`}
                    />
                    <MenuTreeItem {...commonProps} label="Strats" />
                    <MenuTreeItem {...commonProps} label="Covenants" />
                  </MenuTreeItem>
                  <MenuTreeItem {...commonProps} label="Portfolio" />
                  <MenuTreeItem {...commonProps} label={`Strats ${veryLong}`} />
                  <MenuTreeItem {...commonProps} label="Covenants" />
                </MenuTreeItem>
              </MenuTree>
            </Flex>
          );
        }}
      </ComponentGridRenderer>
      <Flex gap="2rem">
        <Flex direction="column" gap="1rem">
          <Divider />
          <SectionHeader>As React Router links</SectionHeader>
          <Flex direction="column" width="18.75rem" gap="0.5rem">
            <SectionText>{`ButtonProps={{ as: Link, to: '.' }}`}</SectionText>
            <MenuTree
              FlexProps={{
                borderWidth: '1px',
                borderColor,
                borderRadius: '0.5rem',
                padding: '0.75rem',
              }}
            >
              <MenuTreeItem
                ButtonProps={{ as: Link, to: '.' }}
                label="Home"
                icon={<Icon icon="home" />}
                isActive
              />
              <MenuTreeItem
                ButtonProps={{ as: Link, to: '.' }}
                label="Tasks"
                icon={<Icon icon="list" />}
              />
              <MenuTreeItem
                ButtonProps={{ as: Link, to: '.' }}
                label="Insights"
                icon={<Icon icon="chart-pie-simple" />}
              >
                <MenuTreeItem
                  ButtonProps={{ as: Link, to: '.' }}
                  label="Tasks"
                />
                <MenuTreeItem
                  ButtonProps={{ as: Link, to: '.' }}
                  label="Portfolio"
                />
                <MenuTreeItem
                  ButtonProps={{ as: Link, to: '.' }}
                  label="Strats"
                />
                <MenuTreeItem
                  ButtonProps={{ as: Link, to: '.' }}
                  label="Covenants"
                />
              </MenuTreeItem>
            </MenuTree>
          </Flex>
        </Flex>
        <Flex direction="column" gap="1rem">
          <Divider />
          <SectionHeader>Multi-line</SectionHeader>
          <Flex direction="column" width="18.75rem" gap="0.5rem">
            <SectionText>{`overflow="multi-line"`}</SectionText>
            <MenuTree
              FlexProps={{
                borderWidth: '1px',
                borderColor,
                borderRadius: '0.5rem',
                padding: '0.75rem',
              }}
            >
              <MenuTreeItem
                colorScheme="gray"
                overflow="multi-line"
                label="Home"
                icon={<Icon icon="home" />}
              />
              <MenuTreeItem
                colorScheme="gray"
                overflow="multi-line"
                label={`Tasks ${veryLong}`}
                icon={<Icon icon="list" />}
              />
              <MenuTreeItem
                isActive
                colorScheme="gray"
                overflow="multi-line"
                label={`Insights ${veryLong}`}
                icon={<Icon icon="chart-pie-simple" />}
              >
                <MenuTreeItem
                  colorScheme="gray"
                  overflow="multi-line"
                  label={`Tasks ${veryLong}`}
                />
                <MenuTreeItem
                  colorScheme="gray"
                  overflow="multi-line"
                  label="Portfolio"
                />
                <MenuTreeItem
                  colorScheme="gray"
                  overflow="multi-line"
                  label="Strats"
                />
                <MenuTreeItem
                  colorScheme="gray"
                  overflow="multi-line"
                  label="Covenants"
                />
              </MenuTreeItem>
            </MenuTree>
          </Flex>
        </Flex>
      </Flex>
    </PageTemplate>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    resetAfterCatch={false}
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <div>Uh oh</div>;
    }}
  >
    <RouteBase />
  </RouteErrorBoundary>
);
